import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { searchApi } from '../../api/search';
import {
  markSearchPending,
  readSearchDraft,
  writeSearchQuery,
} from '../../utils/searchDraft';
import Button from '../Common/Button';
import SearchResult from './SearchResult';
import './Search.css';

function SearchPanel() {
  const { currentStory } = useApp();
  const [query, setQuery] = useState(() => readSearchDraft().query);
  const [results, setResults] = useState({ messages: [], summaries: [], bookmarks: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState(['message', 'summary', 'bookmark']);

  const handleSearch = async (e, text = query, story = currentStory) => {
    e?.preventDefault();
    const nextQuery = String(text || '');
    writeSearchQuery(nextQuery);

    if (!nextQuery.trim()) {
      markSearchPending(false);
      setResults({ messages: [], summaries: [], bookmarks: [] });
      return;
    }

    if (!story) {
      markSearchPending(true);
      return;
    }

    markSearchPending(false);
    setIsSearching(true);
    setError(null);

    try {
      const data = await searchApi.searchStory(story.id, nextQuery, selectedTypes);

      // The search route returns { messages, summaries, bookmarks }.
      // Older callers handed back one flat list; keep that shape working.
      const groupedResults = Array.isArray(data)
        ? {
          messages: data.filter(r => r.source_type === 'message'),
          summaries: data.filter(r => r.source_type === 'summary'),
          bookmarks: data.filter(r => r.source_type === 'bookmark'),
        }
        : {
          messages: data?.messages || [],
          summaries: data?.summaries || [],
          bookmarks: data?.bookmarks || [],
        };

      setResults(groupedResults);
    } catch (err) {
      setError(err.message || 'Failed to search. Please try again.');
      setResults({ messages: [], summaries: [], bookmarks: [] });
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    const draft = readSearchDraft();
    if (!currentStory || !draft.pending || !String(draft.query || '').trim()) return;
    void handleSearch(null, draft.query, currentStory);
    // The draft is the source of truth across a remount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStory?.id]);

  const handleQueryChange = (e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    writeSearchQuery(newQuery);

    // Clear results when query is cleared
    if (!newQuery.trim()) {
      setResults({ messages: [], summaries: [], bookmarks: [] });
      setError(null);
    }
  };

  const handleTypeToggle = (type) => {
    setSelectedTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch(e);
    }
  };

  const totalResults = results.messages.length + results.summaries.length + results.bookmarks.length;
  const hasResults = totalResults > 0;

  return (
    <div className="search-panel">
      <div className="search-panel__header">
        <h2>Search Your Story</h2>
        {currentStory ? (
          <p className="search-panel__story-name">Current story: {currentStory.name}</p>
        ) : (
          <p className="search-panel__no-story">Please select a story to search</p>
        )}
      </div>

      <form className="search-panel__form" onSubmit={handleSearch}>
        <div className="search-input-group">
          <input
            type="text"
            className="search-input"
            placeholder="Search messages, summaries, and bookmarks..."
            value={query}
            onChange={handleQueryChange}
            onKeyPress={handleKeyPress}
            disabled={!currentStory}
          />
          <Button
            type="submit"
            variant="primary"
            disabled={!currentStory || !query.trim() || selectedTypes.length === 0}
            loading={isSearching}
          >
            Search
          </Button>
        </div>

        <div className="search-filters">
          <label className="search-filter-label">Search in:</label>
          <div className="search-filter-options">
            <label className="search-filter-checkbox">
              <input
                type="checkbox"
                checked={selectedTypes.includes('message')}
                onChange={() => handleTypeToggle('message')}
              />
              <span>Messages</span>
            </label>
            <label className="search-filter-checkbox">
              <input
                type="checkbox"
                checked={selectedTypes.includes('summary')}
                onChange={() => handleTypeToggle('summary')}
              />
              <span>Summaries</span>
            </label>
            <label className="search-filter-checkbox">
              <input
                type="checkbox"
                checked={selectedTypes.includes('bookmark')}
                onChange={() => handleTypeToggle('bookmark')}
              />
              <span>Bookmarks</span>
            </label>
          </div>
        </div>
      </form>

      {error && (
        <div className="search-panel__error">
          <span className="search-panel__error-icon">!</span>
          <span>{error}</span>
        </div>
      )}

      {!isSearching && query && hasResults && (
        <div className="search-panel__results-header">
          <h3>Search Results</h3>
          <span className="search-panel__results-count">
            {totalResults} result{totalResults !== 1 ? 's' : ''} found
          </span>
        </div>
      )}

      {!isSearching && query && !hasResults && !error && (
        <div className="search-panel__empty">
          <div className="search-panel__empty-icon">🔍</div>
          <h3>No results found</h3>
          <p>Try adjusting your search query or filters</p>
        </div>
      )}

      {!isSearching && !query && (
        <div className="search-panel__empty">
          <div className="search-panel__empty-icon">💡</div>
          <h3>Start Searching</h3>
          <p>Enter a search query to find messages, summaries, and bookmarks in your story</p>
        </div>
      )}

      {hasResults && (
        <div className="search-panel__results">
          {results.messages.length > 0 && (
            <div className="search-results-section">
              <h4 className="search-results-section__title">
                Messages ({results.messages.length})
              </h4>
              <div className="search-results-list">
                {results.messages.map(result => (
                  <SearchResult
                    key={result.id}
                    result={result}
                    type="message"
                    storyId={currentStory?.id}
                  />
                ))}
              </div>
            </div>
          )}

          {results.summaries.length > 0 && (
            <div className="search-results-section">
              <h4 className="search-results-section__title">
                Summaries ({results.summaries.length})
              </h4>
              <div className="search-results-list">
                {results.summaries.map(result => (
                  <SearchResult
                    key={result.id}
                    result={result}
                    type="summary"
                    storyId={currentStory?.id}
                  />
                ))}
              </div>
            </div>
          )}

          {results.bookmarks.length > 0 && (
            <div className="search-results-section">
              <h4 className="search-results-section__title">
                Bookmarks ({results.bookmarks.length})
              </h4>
              <div className="search-results-list">
                {results.bookmarks.map(result => (
                  <SearchResult
                    key={result.id}
                    result={result}
                    type="bookmark"
                    storyId={currentStory?.id}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchPanel;
